import { Contract } from "ethers";
import EventEmitter from "events";
import { createContext, useContext, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { isInClosingState } from "../../../utils/blockchain-utils";
import { selectBlockchainConfigs } from "../../settings/settings.selectors";
import { useBlockchain } from "./BlockchainProvider";

interface ContextProps {
  onPaymentTransfer: EventEmitter;
  onFeeTransfer: EventEmitter;
}

const abi = [
  "event Transfer(address indexed from, address indexed to, uint256 value)",
];

const PaymentContractTransferEventContext = createContext<ContextProps>(null);

export default function PaymentContractTransferEventProvider({ children }) {
  const { provider } = useBlockchain();
  const blockchainConfigs = useSelector(selectBlockchainConfigs);
  const onPaymentTransfer = useMemo(() => new EventEmitter(), []);
  const onFeeTransfer = useMemo(() => new EventEmitter(), []);
  const paymentTokenContract = useMemo(
    () =>
      blockchainConfigs?.paymentToken && provider
        ? new Contract(blockchainConfigs.paymentToken.address, abi, provider)
        : null,
    [blockchainConfigs?.paymentToken?.address, provider]
  );
  const feeTokenContract = useMemo(
    () =>
      blockchainConfigs?.feeToken &&
      blockchainConfigs?.feeToken?.address !=
        blockchainConfigs?.paymentToken?.address &&
      provider
        ? new Contract(blockchainConfigs.feeToken.address, abi, provider)
        : null,
    [
      blockchainConfigs?.feeToken?.address,
      blockchainConfigs?.paymentToken?.address,
      provider,
    ]
  );

  useEffect(() => {
    paymentTokenContract?.on("Transfer", (from, to, amount, event) => {
      onPaymentTransfer.emit(from, { from, to, amount, event });
      onPaymentTransfer.emit(to, { from, to, amount, event });
    });

    return () => {
      !isInClosingState(provider) && paymentTokenContract?.removeAllListeners();
    };
  }, [paymentTokenContract]);

  useEffect(() => {
    feeTokenContract?.on("Transfer", (from, to, amount, event) => {
      onFeeTransfer.emit(from, { from, to, amount, event });
      onFeeTransfer.emit(to, { from, to, amount, event });
    });

    return () => {
      !isInClosingState(provider) && feeTokenContract?.removeAllListeners();
    };
  }, [feeTokenContract]);

  useEffect(() => {
    return () => {
      provider?.removeAllListeners();
    };
  }, [provider]);

  return (
    <PaymentContractTransferEventContext.Provider
      value={{ onPaymentTransfer, onFeeTransfer }}
    >
      {children}
    </PaymentContractTransferEventContext.Provider>
  );
}

export const usePaymentContractTransferEvent = () =>
  useContext(PaymentContractTransferEventContext);
